import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { CacheBuster, Cacheable } from 'ts-cacheable';
import { ObjectLink } from '../objects-manager.types';
import { CustomObject } from 'app/shared/modules/custom-objects/custom-objects.types';
import { RelatedToType } from '@shared/enums';
import { FieldDef } from '@core/models';

const objectsCacheBuster$ = new Subject<void>();

@Injectable({
  providedIn: 'root',
})
export class ObjectsManagerService {
  public bustObjectsCache(): void {
    objectsCacheBuster$.next();
  }

  constructor(private readonly httpClient: HttpClient) {}

  @Cacheable({ maxAge: 120000, cacheBusterObserver: objectsCacheBuster$ })
  @CacheBuster({
    cacheBusterNotifier: objectsCacheBuster$,
  })
  public get(accountId: number): Observable<CustomObject[]> {
    return this.httpClient.get<{ objects: CustomObject[] }>(`accounts/${accountId}/objects`).pipe(
      map((response) => [
        ...response.objects.map((x) => {
          let apiResponseConfig = { api_path: x.name, api_response_key: x.name };
          let uiConfig = { urlPrefix: x.name };

          switch (x.name) {
            case RelatedToType.Company:
              apiResponseConfig = { api_path: 'companies', api_response_key: 'company' };
              uiConfig = { urlPrefix: 'companies' };
              break;
            case RelatedToType.Contact:
              apiResponseConfig = { api_path: 'contacts', api_response_key: 'contact' };
              uiConfig = { urlPrefix: 'contacts' };
              break;
            case RelatedToType.Property:
              apiResponseConfig = { api_path: 'properties', api_response_key: 'property' };
              uiConfig = { urlPrefix: 'properties' };
              break;
            case RelatedToType.Project:
              apiResponseConfig = { api_path: 'projects', api_response_key: 'project' };
              uiConfig = { urlPrefix: 'deals' };
              break;
            case RelatedToType.PropertyTenant:
              apiResponseConfig = { api_path: 'property_tenants', api_response_key: 'property_tenant' };
              uiConfig = { urlPrefix: 'leases' };
              break;
            default:
              uiConfig = { urlPrefix: (x.id ? x.label : x.name).toLowerCase().replace(/ /g, '-') };
          }

          return { ...x, apiResponseConfig, uiConfig };
        }),
      ]),
    );
  }

  public getById(accountId: number, objectId: number): Observable<CustomObject> {
    return this.httpClient
      .get<{ custom_object: CustomObject }>(`accounts/${accountId}/custom_objects/${objectId}`)
      .pipe(map((response) => response.custom_object));
  }

  @CacheBuster({
    cacheBusterNotifier: objectsCacheBuster$,
  })
  public create(accountId: number, customObject: CustomObject): Observable<CustomObject> {
    return this.httpClient
      .post<{ custom_object: CustomObject }>(`accounts/${accountId}/custom_objects`, customObject)
      .pipe(map((response) => response.custom_object));
  }

  @CacheBuster({
    cacheBusterNotifier: objectsCacheBuster$,
  })
  public patch(accountId: number, objectId: number, customObject: CustomObject): Observable<CustomObject> {
    return this.httpClient
      .patch<{ custom_object: CustomObject }>(`accounts/${accountId}/custom_objects/${objectId}`, {
        custom_object: customObject,
      })
      .pipe(map((response) => response?.custom_object));
  }

  @CacheBuster({
    cacheBusterNotifier: objectsCacheBuster$,
  })
  public createLink(accountId: number, link: ObjectLink): Observable<ObjectLink> {
    return this.httpClient
      .post<{ link: ObjectLink }>(`accounts/${accountId}/links`, { link })
      .pipe(map((response) => response.link));
  }

  public removeLink(accountId: number, link: ObjectLink, force = false): Observable<ObjectLink> {
    return this.httpClient
      .delete<{ link: ObjectLink }>(`accounts/${accountId}/links/${link.id}?force_destroy=${force}`)
      .pipe(map((response) => response.link));
  }

  @CacheBuster({
    cacheBusterNotifier: objectsCacheBuster$,
  })
  public delete(accountId: number, customObjectId: number): Observable<void> {
    return this.httpClient.delete<void>(`accounts/${accountId}/custom_objects/${customObjectId}`);
  }

  createCustomField(fieldDef: FieldDef): Observable<FieldDef> {
    return this.httpClient
      .post<{ custom_field: FieldDef }>(`custom_fields`, { custom_field: fieldDef })
      .pipe(map((response) => response?.custom_field));
  }

  updateCustomField(fieldDef: FieldDef): Observable<FieldDef> {
    return this.httpClient
      .put<{ custom_field: FieldDef }>(`custom_fields/${fieldDef.id}`, {
        custom_field: fieldDef,
      })
      .pipe(map((response) => response.custom_field));
  }

  updateStandardField(fieldDef: FieldDef): Observable<FieldDef> {
    return this.httpClient
      .put<{ field: FieldDef }>(`fields`, {
        standard_field_name: fieldDef.name,
        related_to_type: fieldDef.related_to_type,
        archived: fieldDef.archived,
        show_history: fieldDef.show_history,
        inherited: fieldDef.inherited,
        conditional_styling: fieldDef.conditional_styling,
      })
      .pipe(map((response) => response.field));
  }
}
